/**
 * Onboarding.jsx — first-run wizard.
 *
 * The original dropped a brand-new user straight into an empty feed with a
 * profile page buried behind an avatar menu. Nothing explained why the app
 * wanted their location, so the field read as data harvesting rather than the
 * feature it actually is.
 *
 * Design decisions:
 *   * One question per screen. A single long form is the fastest way to lose
 *     someone on a phone.
 *   * Progress is visible and every step is reversible. Nothing is a trap.
 *   * The username step checks availability live, but the actual claim is a
 *     transactional Cloud Function, so two people cannot take the same handle.
 *   * The location step explains WHY in the field itself, not in a tooltip that
 *     a mobile user cannot open. Disclosure at the point of collection is both
 *     better UX and what GDPR Article 13 requires.
 *   * Consent is granular and unticked by default. A pre-ticked marketing box
 *     is not valid consent under GDPR and is the single most common compliance
 *     failure in a signup flow.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { verifyBeforeUpdateEmail } from 'firebase/auth';
import {
  ArrowLeft, Check, X, Loader2, MapPin, AtSign, Plus, LogOut,
} from 'lucide-react';
import { auth, db, functions } from '../firebase';
// Recorded against the research-panel answer: which policy was in force when it
// was given. Consent to a document that has since changed is not consent now.
import { LEGAL_VERSION } from '../legal/documents';
import { useAuth } from '../hooks/useAuth';
import { Button, Field, useToast, Avatar, InfoTip, Modal } from '../components/ui';
import LocationPicker from '../components/LocationPicker';
import { cleanLine } from '../lib/safe';
import { AVATAR_PALETTE, colourFor } from '../lib/format';
// Shared with Settings — see src/lib/specialties.js. Previously declared inline
// here, which would have drifted the moment either screen was edited.
import { SPECIALTIES } from '../lib/specialties';

const claimUsername = httpsCallable(functions, 'claimUsername');

const ROLE_SUGGESTIONS = [
  'Consultant', 'Specialty Registrar', 'Foundation Doctor', 'GP', 'GP Registrar',
  'Advanced Nurse Practitioner', 'Staff Nurse', 'Clinical Pharmacist',
  'Physician Associate', 'Research Fellow', 'Professor', 'Medical Student',
];

const TITLES = ['Dr', 'Prof', 'Mr', 'Mrs', 'Ms', 'Mx', 'Sr', 'None'];

/**
 * Email comes first, before the username.
 *
 * It is the cheapest step — pre-filled from the account, so most people just
 * press Continue — and it is the one step where being wrong is expensive. If
 * someone signed in with the wrong Google account, they find out immediately
 * rather than after picking a username and typing their name. Confirming who
 * you are before deciding what to be called is also just the right order.
 */
const STEPS = ['welcome', 'email', 'username', 'name', 'role', 'specialty', 'location', 'consent'];

/* ── Username availability ─────────────────────────────────────────────────── */

function useHandleCheck(handle) {
  const [state, setState] = useState({ status: 'idle', message: '' });
  const latest = useRef(0);

  useEffect(() => {
    const value = handle.trim().toLowerCase();

    if (!value) return setState({ status: 'idle', message: '' });
    if (value.length < 3) return setState({ status: 'invalid', message: 'At least 3 characters.' });
    if (value.length > 20) return setState({ status: 'invalid', message: '20 characters maximum.' });
    if (!/^[a-z][a-z0-9_]*$/.test(value)) {
      return setState({
        status: 'invalid',
        message: 'Lowercase letters, numbers and underscores. Must start with a letter.',
      });
    }
    if (value.endsWith('_') || value.includes('__')) {
      return setState({ status: 'invalid', message: 'No trailing or doubled underscores.' });
    }

    setState({ status: 'checking', message: 'Checking…' });
    const token = Date.now();
    latest.current = token;

    const timer = setTimeout(async () => {
      try {
        const snap = await getDoc(doc(db, 'usernames', value));
        // Guard against an out-of-order response overwriting a newer check.
        if (latest.current !== token) return;
        setState(
          snap.exists()
            ? { status: 'taken', message: 'That username is taken.' }
            : { status: 'available', message: 'Available' }
        );
      } catch {
        if (latest.current !== token) return;
        // Never block the user on a failed availability check — the Cloud
        // Function is the real gate and will reject a duplicate anyway.
        setState({ status: 'idle', message: '' });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [handle]);

  return state;
}

/* ── Wizard ────────────────────────────────────────────────────────────────── */

export default function Onboarding() {
  const { user, profile, refreshToken, isSignedIn, loading, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const headingRef = useRef(null);

  // Specialties the user typed themselves. They are rendered as chips in the
  // same list as the built-in ones and selected on creation, because a value
  // you typed and then have to hunt for is a small betrayal.
  const [customOptions, setCustomOptions] = useState([]);
  const [customDraft, setCustomDraft] = useState('');

  const [form, setForm] = useState({
    username: '',
    email: '',
    title: 'Dr',
    firstName: '',
    lastName: '',
    jobTitle: '',
    specialties: [],
    location: '',
    avatarColour: colourFor(user?.uid || 'leeflet'),
    acceptedTerms: false,
    acknowledgedDisclaimer: false,
    marketingEmails: false,
    searchable: true,
    // Unticked, and it stays unticked unless they tick it. See the checkbox.
    researchPanel: false,
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  /**
   * Pre-fill the contact email from the signed-in account.
   *
   * Google sign-in never shows an email field, so a user could previously reach
   * the end of onboarding without ever seeing or confirming the address we hold
   * for them. Now it is shown, confirmable, and changeable.
   */
  useEffect(() => {
    if (user?.email) setForm((f) => (f.email ? f : { ...f, email: user.email }));
  }, [user?.email]);

  /**
   * Add a typed specialty as a selected chip in the list above.
   * De-duplicates case-insensitively against both the built-in list and any
   * the user has already added.
   */
  const addCustomSpecialty = useCallback(() => {
    const value = cleanLine(customDraft, 60);
    if (!value) return;

    const existing = [...SPECIALTIES, ...customOptions]
      .find((s) => s.toLowerCase() === value.toLowerCase());

    if (existing) {
      // Already offered — just select it rather than creating a duplicate.
      setForm((f) => (
        f.specialties.includes(existing)
          ? f
          : { ...f, specialties: [...f.specialties, existing] }
      ));
    } else {
      setCustomOptions((prev) => [...prev, value]);
      setForm((f) => (
        f.specialties.length >= 8 ? f : { ...f, specialties: [...f.specialties, value] }
      ));
    }
    setCustomDraft('');
  }, [customDraft, customOptions]);

  // Which legal document the modal is showing, or null. Opening these in a new
  // tab — or navigating to /legal — unmounted this component and threw away
  // every answer, dumping the user back at step one.
  const [legalDoc, setLegalDoc] = useState(null);
  const handleCheck = useHandleCheck(form.username);

  // Already onboarded? Do not let someone re-run the wizard and confuse
  // themselves about whether their handle changed.
  useEffect(() => {
    if (profile?.onboardingComplete) navigate('/', { replace: true });
  }, [profile, navigate]);

  /**
   * Not signed in? Send them to sign-in.
   *
   * /welcome sits outside RequireAuth (it has to — a user mid-onboarding has no
   * profile yet, which RequireAuth treats as "needs onboarding" and would loop).
   * The consequence was a dead end: land here signed out and you get the wizard
   * with no account behind it, no error, and no route to the sign-in page.
   */
  useEffect(() => {
    if (!loading && !isSignedIn) navigate('/signin', { replace: true });
  }, [loading, isSignedIn, navigate]);

  // Move focus to the new step's heading. Without this, a screen-reader user
  // hears nothing when the step changes and a keyboard user's focus is stranded
  // on a button that no longer exists.
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const current = STEPS[step];

  const canAdvance = useCallback(() => {
    switch (current) {
      case 'welcome': return true;
      case 'username': return handleCheck.status === 'available';
      case 'email': return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim());
      case 'name': return form.firstName.trim().length >= 1;
      case 'role': return form.jobTitle.trim().length >= 2;
      case 'specialty': return form.specialties.length > 0;
      case 'location': return true;   // genuinely optional
      case 'consent': return form.acceptedTerms && form.acknowledgedDisclaimer;
      default: return false;
    }
  }, [current, handleCheck.status, form]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setSaving(true);
    try {
      const specialties = form.specialties.map((v) => cleanLine(v, 60)).filter(Boolean);

      await claimUsername({
        username: form.username.trim().toLowerCase(),
        profile: {
          contactEmail: form.email.trim().toLowerCase(),
          title: form.title === 'None' ? '' : form.title,
          firstName: cleanLine(form.firstName, 50),
          lastName: cleanLine(form.lastName, 50),
          jobTitle: cleanLine(form.jobTitle, 100),
          specialties,
          location: cleanLine(form.location, 120),
          avatarColour: form.avatarColour,
          searchable: form.searchable,
          marketingEmails: form.marketingEmails,
          medicalDisclaimerAcknowledged: form.acknowledgedDisclaimer,
          // The version they were actually shown. A hard-coded date here
          // recorded every signup as agreeing to a document that no longer
          // existed, which is a consent record that proves nothing.
          termsVersion: LEGAL_VERSION,
        },
      });

      /**
       * The research-panel answer goes to the PRIVATE settings document, not
       * the profile — it is nobody else's business, and the profile is world
       * readable.
       *
       * Written here rather than inside claimUsername because the client owns
       * that path and the function does not need to grow a second concern. The
       * timestamp and policy version are recorded exactly as the Settings
       * toggle records them: a consent you cannot date is a consent you cannot
       * rely on.
       *
       * Written whichever way they answered. Being able to show that someone
       * declined matters as much as showing that they agreed.
       */
      await setDoc(
        doc(db, 'users', user.uid, 'private', 'settings'),
        {
          researchPanel: form.researchPanel,
          researchPanelAt: serverTimestamp(),
          researchPanelPolicyVersion: form.researchPanel ? LEGAL_VERSION : null,
        },
        { merge: true },
      ).catch((err) => {
        // The account exists and works. Losing this one preference is not worth
        // failing a signup over — Settings can set it later.
        console.warn('Research panel preference not saved at onboarding', err);
      });

      /**
       * A different address from the one on the account gets a confirmation
       * link before anything is sent to it. Clicking the link makes it the
       * account's email, so every email Clincordant sends — password resets, the
       * morning briefing — goes to an address proven to belong to the person.
       * Until then, the account keeps its current (already confirmed) one.
       */
      const typed = form.email.trim().toLowerCase();
      const current = (auth.currentUser?.email || '').toLowerCase();
      let confirmation = null;   // null = not needed, 'sent', or 'failed'
      if (auth.currentUser && typed && typed !== current) {
        try {
          await verifyBeforeUpdateEmail(auth.currentUser, typed, { url: `${window.location.origin}/` });
          confirmation = 'sent';
        } catch (err) {
          console.warn('Address confirmation not sent', err?.code);
          confirmation = 'failed';
        }
      }

      await refreshToken();
      if (confirmation === 'sent') {
        toast.success(`Welcome to Clincordant, @${form.username}. Check ${typed} for a link to confirm it is yours.`);
      } else if (confirmation === 'failed') {
        toast.success(`Welcome to Clincordant, @${form.username}`);
        toast.error(`We could not send a confirmation to ${typed}, so we will keep using ${current || 'the address you signed up with'}.`);
      } else {
        toast.success(`Welcome to Clincordant, @${form.username}`);
      }
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Onboarding failed', err);
      const message = err?.message?.includes('already')
        ? 'That username was just taken. Please pick another.'
        : err?.message || 'We could not finish setting up your account. Please try again.';
      toast.error(message);
      if (message.includes('username')) setStep(STEPS.indexOf('username'));
    } finally {
      setSaving(false);
    }
  };

  const displayPreview = {
    uid: user?.uid,
    displayName: [form.title === 'None' ? '' : form.title, form.firstName, form.lastName]
      .filter(Boolean).join(' ') || 'Your name',
    avatarColour: form.avatarColour,
  };

  return (
    <div className="onboarding">
      <div className="onboarding__panel">
        <header className="onboarding__head">
          <span className="onboarding__brand">Clincordant</span>
          <span className="onboarding__step" aria-hidden="true">
            {step + 1} of {STEPS.length}
          </span>
        </header>

        {/* Progress is both visual and programmatic. */}
        <div
          className="onboarding__progress"
          role="progressbar"
          aria-valuenow={step + 1}
          aria-valuemin={1}
          aria-valuemax={STEPS.length}
          aria-label="Setup progress"
        >
          <div style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>

        <div className="onboarding__body">
          {current === 'welcome' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">
                Let's set up your briefing
              </h1>
              <p className="onboarding__lead">
                Six quick questions and your first briefing is ready.
              </p>
            </>
          )}

          {current === 'username' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">Choose your username</h1>
              <p className="onboarding__lead">
                This is how colleagues find and mention you. It becomes your profile
                link and cannot be changed later, so pick one you will be happy with.
              </p>

              <div className="handle-field">
                <AtSign size={18} aria-hidden="true" className="handle-field__at" />
                <input
                  id="username"
                  className="handle-field__input"
                  value={form.username}
                  onChange={(e) => set({ username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="arivera"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  maxLength={20}
                  aria-describedby="username-status"
                  aria-invalid={['invalid', 'taken'].includes(handleCheck.status)}
                  autoFocus
                />
                <span className="handle-field__status" aria-hidden="true">
                  {handleCheck.status === 'checking' && <Loader2 size={17} className="spin" />}
                  {handleCheck.status === 'available' && <Check size={17} color="var(--success)" />}
                  {['taken', 'invalid'].includes(handleCheck.status) && <X size={17} color="var(--danger)" />}
                </span>
              </div>

              {/* aria-live so the result is announced as it changes. */}
              <p
                id="username-status"
                aria-live="polite"
                className={
                  handleCheck.status === 'available' ? 'field__hint text-success'
                  : ['taken', 'invalid'].includes(handleCheck.status) ? 'field__error'
                  : 'field__hint'
                }
              >
                {handleCheck.message || 'Lowercase letters, numbers and underscores.'}
              </p>

              {form.username && (
                <p className="onboarding__preview">
                  Your profile: <code>clincordant.com/u/{form.username}</code>
                </p>
              )}
            </>
          )}

          {current === 'email' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">
                Where should we reach you?
              </h1>
              <p className="onboarding__lead">
                We use this for account emails, and for your morning briefing if you
                turn it on. It is never shown on your profile and never shared.
                <InfoTip text="Verifying your clinical role is separate and optional. You can do it later with a work or university email." />
              </p>

              <Field
                label="Email address"
                type="email"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                placeholder="you@example.com"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={200}
                hint={
                  user?.email && form.email.trim().toLowerCase() === user.email.toLowerCase()
                    ? 'This is the address on your account. Change it if you would rather we used a different one.'
                    : 'We will send a link to this address to confirm it is yours.'
                }
                required
                autoFocus
              />

              {/*
                The whole reason email is the first question is to catch a wrong
                account early — someone signed in with a personal Google account
                when they meant to use another. That only helps if there is a way
                out, so here is one.
              */}
              {user?.email && (
                <button
                  type="button"
                  className="link-btn onboarding__switch"
                  onClick={async () => {
                    await logout();
                    navigate('/signin', { replace: true });
                  }}
                >
                  <LogOut size={14} aria-hidden="true" />
                  <span>Not you? Sign in with a different account</span>
                </button>
              )}
            </>
          )}

          {current === 'name' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">What should we call you?</h1>
              <p className="onboarding__lead">
                Your name appears alongside your username on posts and comments.
              </p>

              <fieldset className="onboarding__fieldset">
                <legend className="field__label">Title</legend>
                <div className="chips">
                  {TITLES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`chip${form.title === t ? ' is-selected' : ''}`}
                      aria-pressed={form.title === t}
                      onClick={() => set({ title: t })}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </fieldset>

              <Field
                label="First name"
                value={form.firstName}
                onChange={(e) => set({ firstName: e.target.value })}
                autoComplete="given-name"
                maxLength={50}
              />
              <Field
                label="Last name"
                hint="Optional, but colleagues find it easier to recognise you."
                value={form.lastName}
                onChange={(e) => set({ lastName: e.target.value })}
                autoComplete="family-name"
                maxLength={50}
              />

              <div className="onboarding__avatar-preview">
                <Avatar profile={displayPreview} size={52} />
                <div>
                  <strong>{displayPreview.displayName}</strong>
                  <div className="text-faint" style={{ fontSize: 'var(--text-sm)' }}>@{form.username}</div>
                </div>
              </div>

              <fieldset className="onboarding__fieldset">
                <legend className="field__label">Avatar colour</legend>
                <div className="colour-row">
                  {AVATAR_PALETTE.map((colour) => (
                    <button
                      key={colour}
                      type="button"
                      className={`colour-dot${form.avatarColour === colour ? ' is-selected' : ''}`}
                      style={{ background: colour }}
                      aria-label={`Avatar colour ${colour}`}
                      aria-pressed={form.avatarColour === colour}
                      onClick={() => set({ avatarColour: colour })}
                    />
                  ))}
                </div>
              </fieldset>
            </>
          )}

          {current === 'role' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">What is your role?</h1>
              <p className="onboarding__lead">
                This changes how we pitch each summary — a consultant and a
                foundation doctor need different things from the same paper.
              </p>

              <Field
                label="Job title"
                value={form.jobTitle}
                onChange={(e) => set({ jobTitle: e.target.value })}
                placeholder="e.g. Consultant Geriatrician"
                maxLength={100}
                autoFocus
              />

              <p className="field__label" style={{ marginBottom: 8 }}>Or pick a starting point</p>
              <div className="chips">
                {ROLE_SUGGESTIONS.map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={`chip${form.jobTitle === role ? ' is-selected' : ''}`}
                    onClick={() => set({ jobTitle: role })}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </>
          )}

          {current === 'specialty' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">
                Which areas do you work in?
              </h1>
              <p className="onboarding__lead">
                Pick up to eight. These drive the literature search that builds your
                daily cards &mdash; you can change them any time.
              </p>

              <div className="chips" role="group" aria-label="Specialties">
                {[...SPECIALTIES, ...customOptions].map((s) => {
                  const selected = form.specialties.includes(s);
                  const atLimit = form.specialties.length >= 8 && !selected;
                  const isCustom = customOptions.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      className={`chip${selected ? ' is-selected' : ''}${isCustom ? ' chip--custom' : ''}`}
                      aria-pressed={selected}
                      disabled={atLimit}
                      onClick={() =>
                        set({
                          specialties: selected
                            ? form.specialties.filter((x) => x !== s)
                            : [...form.specialties, s],
                        })
                      }
                    >
                      {s}
                    </button>
                  );
                })}
              </div>

              <p className="field__hint" aria-live="polite" style={{ marginTop: 10 }}>
                {form.specialties.length} of 8 selected
              </p>

              {/*
                Adding your own appends a chip to the list above and selects it,
                rather than living in a separate field. A value you typed and
                then have to hunt for reads as the app ignoring you.
              */}
              <div className="field" style={{ marginTop: 18 }}>
                <label htmlFor="custom-specialty" className="field__label">Not listed?</label>
                <div className="chip-add">
                  <input
                    id="custom-specialty"
                    className="field__input"
                    value={customDraft}
                    onChange={(e) => setCustomDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addCustomSpecialty(); }
                    }}
                    placeholder="e.g. Neurorehabilitation"
                    maxLength={60}
                    aria-describedby="custom-specialty-hint"
                  />
                  <Button
                    variant="secondary"
                    onClick={addCustomSpecialty}
                    disabled={!customDraft.trim() || form.specialties.length >= 8}
                    icon={<Plus size={15} />}
                  >
                    Add
                  </Button>
                </div>
                <p id="custom-specialty-hint" className="field__hint">
                  Subspecialties work well here. Press Enter or Add and it joins the
                  list above, already selected.
                </p>
              </div>
            </>
          )}

          {current === 'location' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">
                Where do you practise?
              </h1>

              {/* The explanation sits in the flow, not behind a hover tooltip —
                  a mobile user can never open a hover tooltip, and this is
                  exactly the disclosure GDPR Art. 13 asks for. */}
              <div className="callout callout--info">
                <MapPin size={18} aria-hidden="true" />
                <div>
                  <strong>Why we ask</strong>
                  <p>
                    We use your region to weight research by local relevance — for
                    example, prioritising antimicrobial resistance data that matches
                    your area's resistance patterns, or studies whose population
                    resembles the one you treat.
                  </p>
                  <p>
                    A city or region is enough. We never request or store your precise
                    location, and it is never shown on your profile unless you choose
                    to display it.
                  </p>
                </div>
              </div>

              <LocationPicker
                value={form.location}
                onChange={(label) => set({ location: label })}
                label="City, borough or region"
                hint="Optional. Leave blank and we will keep recommendations global."
              />
            </>
          )}

          {current === 'consent' && (
            <>
              <h1 ref={headingRef} tabIndex={-1} className="onboarding__title">Two things before you start</h1>

              <div className="callout callout--warning">
                <strong>Clincordant is a current-awareness tool, not clinical guidance.</strong>
                <p>
                  Summaries and relevance scores are generated by AI from published
                  abstracts and can be incomplete or wrong. Always read the source paper
                  and apply your own clinical judgement before changing patient care.
                  Clincordant is not a substitute for professional advice, formal guidelines,
                  or your local protocols.
                </p>
              </div>

              {/* Both unticked by default. A pre-ticked consent box is not consent. */}
              <label className="consent">
                <input
                  type="checkbox"
                  checked={form.acknowledgedDisclaimer}
                  onChange={(e) => set({ acknowledgedDisclaimer: e.target.checked })}
                />
                <span>
                  I understand that Clincordant provides AI-generated summaries for awareness
                  only and does not constitute medical advice.
                </span>
              </label>

              <label className="consent">
                <input
                  type="checkbox"
                  checked={form.acceptedTerms}
                  onChange={(e) => set({ acceptedTerms: e.target.checked })}
                />
                <span>
                  I agree to the{' '}
                  <button type="button" className="link-btn" onClick={() => setLegalDoc('terms')}>
                    Terms of Service
                  </button>,{' '}
                  <button type="button" className="link-btn" onClick={() => setLegalDoc('privacy')}>
                    Privacy Policy
                  </button>{' '}and{' '}
                  <button type="button" className="link-btn" onClick={() => setLegalDoc('community')}>
                    Community Guidelines
                  </button>, and I confirm I will not post patient-identifiable
                  information.
                </span>
              </label>

              <hr className="onboarding__rule" />

              <label className="consent consent--optional">
                <input
                  type="checkbox"
                  checked={form.marketingEmails}
                  onChange={(e) => set({ marketingEmails: e.target.checked })}
                />
                <span>
                  Email me a weekly digest and occasional product updates.
                  <span className="text-faint"> Optional — unsubscribe any time.</span>
                </span>
              </label>

              <label className="consent consent--optional">
                <input
                  type="checkbox"
                  checked={form.searchable}
                  onChange={(e) => set({ searchable: e.target.checked })}
                />
                <span>
                  Let colleagues find me by name or username in search.
                  <InfoTip text="Turn this off and your profile is only reachable by direct link. You can change it in Settings." />
                </span>
              </label>

              {/*
                The research panel, asked here rather than only in Settings.

                A setting that only lives five screens deep in a menu is one
                nobody finds. Asking plainly at the one moment someone is
                already reading about what the platform does with their data is
                both the honest place and the only place with any uptake. The
                label says "publish or sell" because consent has to name what
                it is for; the detail lives in the tooltip and the policy.

                Unticked, and it stays unticked if they skip past it. A
                pre-ticked box is not consent under GDPR, and on the one setting
                the business depends on, a consent that would not survive being
                questioned is worth less than no consent at all.
              */}
              <label className="consent consent--optional">
                <input
                  type="checkbox"
                  checked={form.researchPanel}
                  onChange={(e) => set({ researchPanel: e.target.checked })}
                />
                <span>
                  Include me in aggregate research statistics, which Clincordant may publish or sell.
                  <span className="text-faint"> Counts only, never your name, posts, messages or searches.</span>
                  <InfoTip text="Off unless you tick it. Figures look like “41% of respiratory trainees opened a paper on this topic”, and no group is ever small enough to identify you. Change it at any time in Settings → Privacy; the Privacy Policy lists exactly what is counted." />
                </span>
              </label>
            </>
          )}
        </div>

        <footer className="onboarding__foot">
          {step > 0 ? (
            <Button variant="ghost" onClick={back} icon={<ArrowLeft size={16} />} disabled={saving}>
              Back
            </Button>
          ) : <span />}

          {current === 'consent' ? (
            <Button onClick={finish} disabled={!canAdvance() || saving} loading={saving}>
              Start reading
            </Button>
          ) : (
            <Button onClick={next} disabled={!canAdvance()}>
              {current === 'location' && !form.location ? 'Skip for now' : 'Continue'}
            </Button>
          )}
        </footer>

        {/*
          Shown in a modal rather than a new tab. Navigating away from an
          in-progress wizard destroys its state, and a new tab on mobile buries
          the half-finished signup behind another window. These are short
          summaries with a link to the full text for anyone who wants it.
        */}
        <Modal
          open={!!legalDoc}
          onClose={() => setLegalDoc(null)}
          title={LEGAL_SUMMARIES[legalDoc]?.title ?? ''}
          size="md"
          footer={<Button onClick={() => setLegalDoc(null)}>Close</Button>}
        >
          {legalDoc && (
            <div className="legal-summary">
              <p className="legal-summary__lead">{LEGAL_SUMMARIES[legalDoc].lead}</p>
              <ul>
                {LEGAL_SUMMARIES[legalDoc].points.map((point) => <li key={point}>{point}</li>)}
              </ul>
              <p className="legal-summary__full">
                This is a plain-English summary. The full document is at{' '}
                <Link to={`/legal/${legalDoc}`} onClick={() => setLegalDoc(null)}>
                  /legal/{legalDoc}
                </Link>{' '}
                and you can read it any time from Settings.
              </p>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}

/**
 * Plain-English summaries shown during signup.
 *
 * Deliberately short. Nobody reads a 4,000-word Terms of Service at the moment
 * they are trying to create an account, and pretending otherwise makes consent
 * a fiction. These cover what actually affects the user; the full documents
 * remain one link away.
 */
const LEGAL_SUMMARIES = {
  terms: {
    title: 'Terms of Service — the short version',
    lead: 'What you are agreeing to by using Clincordant.',
    points: [
      'Clincordant is for healthcare professionals and healthcare students. It is not a source of medical advice for the public.',
      'Never post patient-identifiable information. Not names, not dates of birth, not NHS numbers, not identifiable images. This is the single most important rule here.',
      'You are responsible for what you post, and you keep ownership of it. You grant us permission to display it on the platform.',
      'Do not impersonate anyone or misrepresent your professional standing.',
      'We can suspend accounts that break these rules. You can delete yours at any time from Settings.',
      'Clincordant is provided as-is. We do not guarantee the accuracy of AI-generated summaries.',
    ],
  },
  privacy: {
    title: 'Privacy Policy — the short version',
    lead: 'What we collect, why, and what you control.',
    points: [
      'We collect what you give us: email, name, username, role, specialties and optionally your region.',
      'Your specialty, role and region are used to choose which research appears in your briefing. Region is never shown on your profile unless you choose to display it.',
      'Your data is stored in the European Union (Belgium) on Google Cloud infrastructure.',
      'Abstracts are sent to OpenAI to generate summaries. Your name and email are never sent — only your role, specialties and region.',
      'We never sell your data, and we do not run advertising.',
      'You can export everything we hold, or delete your account entirely, from Settings. Deletion is permanent.',
      'Messages you have sent to other people remain in their copy of the conversation after you delete your account, shown as from a deleted user.',
    ],
  },
  community: {
    title: 'Community Guidelines — the short version',
    lead: 'How we expect people to behave here.',
    points: [
      'No patient-identifiable information, in any form, ever.',
      'Disagree with the argument, not the person. Robust clinical debate is welcome; personal abuse is not.',
      'Do not present yourself as holding qualifications or registration you do not have.',
      'Do not give individual medical advice to members of the public.',
      'Declare relevant commercial interests when you post about a product or intervention.',
      'Report anything that concerns you. Reports are seen only by moderators, and the person reported is never told who reported them.',
    ],
  },
};

export const ONBOARDING_CSS = `
/**
 * Onboarding always wears the brand green, whatever accent the account has.
 *
 * Two reasons, and the second is the one that matters. First, this is the only
 * screen where someone is meeting Clincordant rather than using it, and it should
 * look like Clincordant rather than like whichever colour they happened to pick
 * last time. Second, the accent is an account-level preference that arrives
 * over the network, so during sign-in this page can be painting before it
 * lands — and a screen that changes colour halfway through reads as broken.
 *
 * Custom properties inherit, and a declaration here beats the inline ones
 * applyAccent writes on <html>, so this subtree is simply immune to it.
 * Values are ACCENTS.forest from lib/theme.js — keep them in step.
 */
.onboarding {
  --primary: #1A5449;
  --primary-hover: #144039;
  --primary-text: #14453C;
  --primary-tint: #E7F0EB;

  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--bg);
}
[data-theme='dark'] .onboarding {
  --primary: #5FB3A1;
  --primary-hover: #74C4B3;
  --primary-text: #7FCBB9;
  --primary-tint: #172C26;
}
.onboarding__panel {
  width: 100%;
  max-width: 520px;
  background: var(--card);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 40px);
}
@media (max-width: 560px) {
  .onboarding { padding: 0; }
  .onboarding__panel { max-height: 100dvh; border: none; border-radius: 0; box-shadow: none; }
}

.onboarding__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px 12px;
}
.onboarding__brand {
  font-family: var(--font-display); font-style: italic; font-weight: 600;
  font-size: var(--text-lg); color: var(--primary-text);
}
.onboarding__step { font-family: var(--font-mono); font-size: var(--text-xs); color: var(--ink-faint); }

.onboarding__progress { height: 3px; background: var(--bg-alt); margin: 0 24px; border-radius: 2px; overflow: hidden; }
.onboarding__switch {
  display: inline-flex; align-items: center; gap: 7px;
  margin-top: 18px; font-size: var(--text-xs); color: var(--ink-faint);
  min-height: 34px;
}
.onboarding__switch svg { display: block; flex: 0 0 auto; }
.onboarding__switch span { display: block; line-height: 1; }

.onboarding__progress > div { height: 100%; background: var(--primary); transition: width 260ms ease; }

.onboarding__body { padding: 24px; overflow-y: auto; flex: 1; }
.onboarding__title { font-family: var(--font-display); font-size: var(--text-2xl); margin: 0 0 10px; line-height: 1.25; }
.onboarding__title:focus { outline: none; }
.onboarding__lead { color: var(--ink-soft); font-size: var(--text-sm); line-height: 1.6; margin: 0 0 22px; }

.onboarding__fieldset { border: none; padding: 0; margin: 0 0 20px; }
.onboarding__fieldset legend { padding: 0; margin-bottom: 8px; }

.onboarding__avatar-preview {
  display: flex; align-items: center; gap: 14px;
  padding: 14px; margin: 4px 0 20px;
  background: var(--bg-alt); border-radius: var(--radius);
  border: 1px solid var(--line-strong);
}
.onboarding__preview { margin-top: 14px; font-size: var(--text-sm); color: var(--ink-soft); }
.onboarding__preview code {
  font-family: var(--font-mono); font-size: var(--text-xs);
  background: var(--bg-alt); padding: 3px 7px; border-radius: 5px;
}
.onboarding__rule { border: none; border-top: 1px solid var(--line); margin: 20px 0; }

.onboarding__foot {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 24px calc(16px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--line);
}

.handle-field {
  display: flex; align-items: center; gap: 8px;
  padding: 0 13px;
  border: 1px solid var(--line-ui);
  border-radius: var(--radius);
  background: var(--card);
}
.handle-field:focus-within { border-color: var(--primary); }
.handle-field__at { color: var(--ink-faint); flex-shrink: 0; }
.handle-field__input {
  flex: 1; min-width: 0;
  min-height: var(--tap);
  border: none; background: none; outline: none;
  color: var(--ink); font: inherit; font-size: var(--text-base);
  font-family: var(--font-mono);
}
.handle-field__status { display: grid; place-items: center; width: 20px; flex-shrink: 0; }

.colour-row { display: flex; flex-wrap: wrap; gap: 10px; }
.colour-dot {
  width: 32px; height: 32px;
  border-radius: 50%; border: 2px solid transparent;
  cursor: pointer; position: relative;
}
.colour-dot::before { content: ''; position: absolute; inset: -6px; }
.colour-dot.is-selected { box-shadow: 0 0 0 2px var(--card), 0 0 0 4px var(--ink); }

.callout {
  display: flex; gap: 12px;
  padding: 14px 16px; margin-bottom: 20px;
  border-radius: var(--radius);
  font-size: var(--text-sm); line-height: 1.55;
  /* Inherited rather than assumed. A callout sits on --card here and on
     --bg elsewhere, and both carry --ink at AA or better. */
  color: var(--ink);
  border: 1px solid var(--line-strong);
}
.callout p { margin: 6px 0 0; color: var(--ink-soft); }
.callout--info { background: var(--primary-tint); border-color: color-mix(in srgb, var(--primary) 30%, var(--line-strong)); }
.callout--info svg { color: var(--primary-text); flex-shrink: 0; margin-top: 2px; }
.callout--warning { background: var(--warning-bg); border-color: color-mix(in srgb, var(--warning) 40%, var(--line-strong)); border-left: 3px solid var(--warning); display: block; }

.consent {
  display: flex; gap: 11px; align-items: flex-start;
  padding: 12px 0;
  font-size: var(--text-sm); line-height: 1.55;
  cursor: pointer;
}
.consent input {
  width: 20px; height: 20px; margin: 1px 0 0;
  flex-shrink: 0; cursor: pointer;
  accent-color: var(--primary);
}
.consent--optional { color: var(--ink-soft); }
.text-success { color: var(--success); }

/* Typed specialties, so they are visibly distinguishable from the built-ins. */
.chip--custom { border-style: dashed; }
.chip--custom.is-selected { border-style: solid; }

.chip-add { display: flex; gap: 8px; align-items: stretch; }
.chip-add .field__input { flex: 1; min-width: 0; }
.chip-add .btn { flex-shrink: 0; }

/* Inline buttons inside the consent text. They open the legal modal, so they
   must read as links rather than as buttons interrupting a sentence. */
.consent .link-btn {
  font: inherit; font-weight: 600;
  color: var(--primary-text);
  padding: 0; vertical-align: baseline;
}

.legal-summary__lead {
  margin: 0 0 14px; font-size: var(--text-sm);
  color: var(--ink-soft); line-height: 1.6;
}
.legal-summary ul { margin: 0; padding-left: 20px; }
.legal-summary li {
  font-size: var(--text-sm); line-height: 1.65; margin-bottom: 10px;
}
.legal-summary__full {
  margin: 16px 0 0; padding-top: 14px;
  border-top: 1px solid var(--line);
  font-size: var(--text-xs); color: var(--ink-faint); line-height: 1.6;
}
`;
